import { z } from "zod"

/**
 * Subject options for the contact form.
 * Single source of truth - used by form, API, and email template.
 */
export const SUBJECT_OPTIONS = [
  { value: "general", label: "General Inquiry" },
  { value: "partnership", label: "Partnership Opportunity" },
  { value: "press", label: "Press/Media" },
  { value: "support", label: "Technical Support" },
  { value: "feedback", label: "Feedback" },
  { value: "other", label: "Other" },
] as const

/**
 * Type for valid subject values.
 */
export type SubjectValue = (typeof SUBJECT_OPTIONS)[number]["value"]

/**
 * Lookup map from subject value to label.
 * Type-safe: only valid SubjectValue keys are allowed.
 */
export const SUBJECT_LABELS = Object.fromEntries(SUBJECT_OPTIONS.map(({ value, label }) => [value, label])) as Record<
  SubjectValue,
  string
>

/**
 * Valid subject values as a tuple for z.enum().
 */
const SUBJECT_VALUES = SUBJECT_OPTIONS.map((opt) => opt.value) as [SubjectValue, ...SubjectValue[]]

/**
 * Zod schema for contact form validation.
 * Shared between client and server for consistent validation.
 */
export const contactSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters"),
  email: z.string().email("Please enter a valid email address"),
  phone: z.string().optional(),
  company: z.string().optional(),
  subject: z.enum(SUBJECT_VALUES, {
    error: "Please select a subject from the dropdown",
  }),
  message: z
    .string()
    .min(10, "Message must be at least 10 characters")
    .max(2000, "Message must be less than 2000 characters"),
  // Honeypot field - should always be empty. Bots often fill all fields.
  website: z.string().optional(),
})

export type ContactFormData = z.infer<typeof contactSchema>
